import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Loader2, Send, Wand2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCorporateAvailableAddons,
  getCorporateAvailablePrograms,
  inviteCorporateEnrollment,
} from '@/apis/corporates';
import { createUserAddOnEnrollment, updateUser } from '@/apis/users';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CountryCodeSelector } from '@/components/ui/country-code-selector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { DEFAULT_COUNTRY_CODE } from '@/constants/countryCodes';
import { logger } from '@/lib/logger';
import { RootState } from '@/redux/store';
import { dateOfBirthSchema } from '@/schemas/userProfile.schema';
import type { CorporateAvailableAddon, CorporateAvailableProgram } from '@/types/corporate';
import { getAccessToken } from '@/utils/auth';
import {
  applyInvitePhoneCountryChange,
  generateMagicFillEmail,
  generateMagicFillPhone,
  getPhoneMaxLength,
  getSafeErrorMessage,
  isValidEmail,
  parseInviteFieldErrors,
  phoneValidationMessage,
  withProfileSaveRetry,
  type InviteEmployeeFieldErrors,
} from './inviteEmployeeValidation';

const FIRST_NAMES = [
  'Avery',
  'Jordan',
  'Morgan',
  'Taylor',
  'Riley',
  'Drew',
  'Emerson',
  'Parker',
  'Quinn',
  'Skyler',
];
const LAST_NAMES = [
  'Rivera',
  'Hayes',
  'Nolan',
  'Sage',
  'Bennett',
  'Finley',
  'Ellis',
  'Rowan',
  'Sawyer',
  'Monroe',
];
const DEPARTMENTS = [
  'Engineering',
  'Product',
  'People Operations',
  'Customer Success',
  'Finance',
  'Marketing',
  'Sales',
];

const getRandomItem = <T,>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

const generateRandomDOB = () => {
  const start = new Date(1975, 0, 1).getTime();
  const end = new Date(2000, 11, 31).getTime();
  const date = new Date(start + Math.random() * (end - start));
  return date.toISOString().split('T')[0];
};

interface InviteEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporateId: string;
  onSuccess?: () => void;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-red-500">{message}</p> : null;

export const InviteEmployeeModal = ({
  isOpen,
  onClose,
  corporateId,
  onSuccess,
}: InviteEmployeeModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const userRole = useSelector((state: RootState) => state.user?.role);
  const isCorporateAdmin = userRole === 'CORPORATE_ADMIN';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [programId, setProgramId] = useState('');
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<InviteEmployeeFieldErrors>({});
  const [dialogContent, setDialogContent] = useState<HTMLElement | null>(null);

  const { data: availableProgramsData, isLoading: isLoadingPrograms } = useQuery({
    queryKey: ['corporate-available-programs', corporateId, isOpen],
    queryFn: async () => {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) throw new Error('No authentication token');
      return getCorporateAvailablePrograms(corporateId, token, {
        is_active: true,
      });
    },
    enabled: isOpen && !!corporateId,
  });

  const { data: availableAddonsData, isLoading: isLoadingAddons } = useQuery({
    queryKey: ['corporate-available-addons', corporateId, isOpen],
    queryFn: async () => {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) throw new Error('No authentication token');
      return getCorporateAvailableAddons(corporateId, token, {
        is_active: true,
      });
    },
    enabled: isOpen && !!corporateId,
  });

  const dateOfBirthBounds = useMemo(() => {
    const today = new Date();
    const formatInputDate = (date: Date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    };
    return {
      min: formatInputDate(new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())),
      max: formatInputDate(new Date(today.getFullYear() - 10, today.getMonth(), today.getDate())),
    };
  }, []);

  const corporatePrograms: CorporateAvailableProgram[] = useMemo(() => {
    if (!availableProgramsData) return [];
    return Array.isArray(availableProgramsData)
      ? availableProgramsData
      : availableProgramsData.results || [];
  }, [availableProgramsData]);

  const corporateAddons: CorporateAvailableAddon[] = useMemo(() => {
    if (!availableAddonsData) return [];
    return Array.isArray(availableAddonsData)
      ? availableAddonsData
      : availableAddonsData.results || [];
  }, [availableAddonsData]);

  const handleMagicFill = () => {
    const randomFirst = getRandomItem(FIRST_NAMES);
    const randomLast = getRandomItem(LAST_NAMES);
    setFirstName(randomFirst);
    setLastName(randomLast);
    setEmail(generateMagicFillEmail(randomFirst, randomLast));
    setDepartment(getRandomItem(DEPARTMENTS));
    setPhoneCountryCode(DEFAULT_COUNTRY_CODE);
    setPhoneNumber(generateMagicFillPhone());
    setDateOfBirth(generateRandomDOB());
    setFieldErrors({});
    if (corporatePrograms.length > 0) {
      const preferredProgram =
        corporatePrograms.find((program) => program.is_default) ?? corporatePrograms.at(0);
      if (preferredProgram?.program) {
        setProgramId(preferredProgram.program);
      }
    }
    if (corporateAddons.length > 0) {
      const sampled = corporateAddons
        .filter(() => Math.random() > 0.5)
        .map((addon) => String(addon.addon_details?.addon_id || addon.addon || addon.id));
      setSelectedAddonIds(sampled);
    } else {
      setSelectedAddonIds([]);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setDepartment('');
    setPhoneNumber('');
    setPhoneCountryCode(DEFAULT_COUNTRY_CODE);
    setDateOfBirth('');
    setProgramId('');
    setSelectedAddonIds([]);
    setFieldErrors({});
  };

  const clearFieldError = (field: keyof InviteEmployeeFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = (): boolean => {
    const nextErrors: InviteEmployeeFieldErrors = {};

    if (!firstName.trim()) {
      nextErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      nextErrors.lastName = 'Last name is required';
    }

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      nextErrors.email = 'Please enter a valid email address';
    }

    const phoneDigits = phoneNumber.replace(/\D/g, '');
    const phoneMaxLength = getPhoneMaxLength(phoneCountryCode);
    const phoneError = phoneValidationMessage(phoneNumber, phoneCountryCode);
    if (phoneError || phoneDigits.length > phoneMaxLength) {
      nextErrors.phoneNumber =
        phoneError ?? `Enter at most ${phoneMaxLength} digits for the selected country code`;
    }

    if (!dateOfBirth) {
      nextErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const parsedDateOfBirth = dateOfBirthSchema.safeParse(dateOfBirth);
      if (!parsedDateOfBirth.success) {
        nextErrors.dateOfBirth =
          parsedDateOfBirth.error.issues[0]?.message || 'Date of birth is not valid';
      }
    }

    if (!programId) {
      nextErrors.programId = 'Please select a program';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) throw new Error('No authentication token');

      // Step 1: Invite via backend (creates user + enrollment + sends email)
      const invitation = await inviteCorporateEnrollment(
        {
          corporate: corporateId,
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          department: department.trim() || undefined,
          program: programId,
        },
        token
      );

      const userProfileId = invitation.user;
      const enrollmentId = invitation.enrollment_id;

      if (!userProfileId || !enrollmentId) {
        throw new Error('Invitation created without user or enrollment reference');
      }

      const postInviteIssues: string[] = [];
      if (!isCorporateAdmin) {
        try {
          await withProfileSaveRetry(() =>
            updateUser(
              {
                user_profile_id: userProfileId,
                phone_number: phoneNumber.trim(),
                phone_country_code: phoneCountryCode || DEFAULT_COUNTRY_CODE,
                date_of_birth: dateOfBirth,
              },
              token
            )
          );
        } catch (profileError: unknown) {
          logger.error('Failed to update invited employee profile', {
            userProfileId,
            enrollmentId,
            error: profileError,
          });
          const profileFieldErrors = parseInviteFieldErrors(profileError);
          const profileDetail = [profileFieldErrors.phoneNumber, profileFieldErrors.dateOfBirth]
            .filter(Boolean)
            .join(' ');
          postInviteIssues.push(
            profileDetail
              ? `Profile details were not saved. ${profileDetail}`
              : `Profile details were not saved for user ${userProfileId}. Update that profile with the phone number and date of birth.`
          );
        }

        if (selectedAddonIds.length > 0) {
          const today = new Date().toISOString().split('T')[0];
          const addonAssignments = selectedAddonIds.map(async (addonId) => {
            const addon = corporateAddons.find(
              (entry) =>
                String(entry.addon) === addonId ||
                entry.addon_details?.addon_id === addonId ||
                String(entry.id) === addonId
            );

            return withProfileSaveRetry(() =>
              createUserAddOnEnrollment(
                {
                  user: userProfileId,
                  addon_id: addon?.addon_details?.addon_id || addon?.addon || addonId,
                  addon_name: addon?.addon_details?.name || addon?.addon_name || 'Add-on',
                  start_date: today,
                  is_active: true,
                },
                token
              )
            );
          });

          const addonResults = await Promise.allSettled(addonAssignments);
          const failedAddonIds = addonResults.flatMap((result, index) =>
            result.status === 'rejected' ? [selectedAddonIds[index]] : []
          );

          if (failedAddonIds.length > 0) {
            logger.error('Failed to assign one or more add-ons during invite', {
              userProfileId,
              enrollmentId,
              failedAddonIds,
            });
            postInviteIssues.push(
              `${failedAddonIds.length} add-on${failedAddonIds.length > 1 ? 's were' : ' was'} not assigned for user ${userProfileId}.`
            );
          }
        }
      } else if (selectedAddonIds.length > 0) {
        postInviteIssues.push(
          'Please ask the Superclinic team to enable add-ons after the employee registers.'
        );
      }

      const successTitle = isCorporateAdmin ? 'Invitation email sent' : 'Employee invited';
      const successMessage = isCorporateAdmin
        ? 'The employee will appear once they register.'
        : 'Employee invited successfully.';
      const warningMessage = postInviteIssues.join(' ');
      if (warningMessage) {
        toast({
          title: `${successTitle} with warnings`,
          description: `${successMessage} ${warningMessage}`.trim(),
          variant: 'destructive',
        });
      } else {
        toast({ title: successTitle, description: successMessage });
      }
      queryClient.invalidateQueries({ queryKey: ['corporate-enrollments'] });
      queryClient.invalidateQueries({
        queryKey: ['corporate-enrollments', corporateId],
      });
      onSuccess?.();
      handleClose();
    } catch (error: unknown) {
      logger.error('Error creating corporate employee:', error);
      const serverFieldErrors = parseInviteFieldErrors(error);
      if (Object.keys(serverFieldErrors).length > 0) {
        setFieldErrors((current) => ({ ...current, ...serverFieldErrors }));
      }
      toast({
        title: 'Failed to create employee',
        description: getSafeErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        ref={setDialogContent}
        className="!top-4 flex h-auto max-h-[calc(100dvh-2rem)] w-full max-w-2xl !translate-y-0 flex-col gap-0 overflow-visible bg-white p-0 sm:!top-6"
        data-testid="corporateInvite_modal"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5 pr-14">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle data-testid="corporateInvite_title">Invite Employee</DialogTitle>
            <DialogDescription>
              Create a corporate member profile, assign a program, and send the invite.
            </DialogDescription>
          </DialogHeader>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="shrink-0 gap-2"
            onClick={handleMagicFill}
            disabled={isSubmitting}
          >
            <Wand2 className="h-4 w-4" />
            Magic Fill
          </Button>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex min-h-0 flex-1 flex-col"
          data-testid="corporateInvite_form"
        >
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearFieldError('firstName');
                  }}
                  placeholder="John"
                  className={fieldErrors.firstName ? 'border-red-500' : ''}
                  aria-invalid={!!fieldErrors.firstName}
                />
                <FieldError message={fieldErrors.firstName} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearFieldError('lastName');
                  }}
                  placeholder="Doe"
                  className={fieldErrors.lastName ? 'border-red-500' : ''}
                  aria-invalid={!!fieldErrors.lastName}
                />
                <FieldError message={fieldErrors.lastName} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearFieldError('email');
                }}
                placeholder="employee@company.com"
                className={fieldErrors.email ? 'border-red-500' : ''}
                aria-invalid={!!fieldErrors.email}
              />
              <FieldError message={fieldErrors.email} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[9.5rem_1fr]">
              <div className="space-y-2">
                <Label htmlFor="phoneCode">Code</Label>
                <CountryCodeSelector
                  value={{ code: phoneCountryCode || DEFAULT_COUNTRY_CODE }}
                  onValueChange={(country) => {
                    const nextCode = country.code || DEFAULT_COUNTRY_CODE;
                    setPhoneCountryCode(nextCode);
                    setPhoneNumber((current) => applyInvitePhoneCountryChange(current, nextCode));
                    clearFieldError('phoneNumber');
                  }}
                  menuPortalTarget={dialogContent}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phoneNumber}
                  maxLength={getPhoneMaxLength(phoneCountryCode)}
                  onChange={(e) => {
                    setPhoneNumber(applyInvitePhoneCountryChange(e.target.value, phoneCountryCode));
                    clearFieldError('phoneNumber');
                  }}
                  placeholder="9876543210"
                  className={fieldErrors.phoneNumber ? 'border-red-500' : ''}
                  aria-invalid={!!fieldErrors.phoneNumber}
                />
                <FieldError message={fieldErrors.phoneNumber} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dob">
                  Date of Birth <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dob"
                  type="date"
                  min={dateOfBirthBounds.min}
                  max={dateOfBirthBounds.max}
                  value={dateOfBirth}
                  onChange={(e) => {
                    setDateOfBirth(e.target.value);
                    clearFieldError('dateOfBirth');
                  }}
                  className={fieldErrors.dateOfBirth ? 'border-red-500' : ''}
                  aria-invalid={!!fieldErrors.dateOfBirth}
                />
                <FieldError message={fieldErrors.dateOfBirth} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="Engineering"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="program">
                Program <span className="text-red-500">*</span>
              </Label>
              <Select
                value={programId}
                onValueChange={(value) => {
                  setProgramId(value);
                  clearFieldError('programId');
                }}
              >
                <SelectTrigger
                  id="program"
                  className={fieldErrors.programId ? 'border-red-500' : ''}
                  aria-invalid={!!fieldErrors.programId}
                >
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingPrograms ? (
                    <SelectItem value="loading" disabled>
                      Loading programs...
                    </SelectItem>
                  ) : corporatePrograms.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No programs available
                    </SelectItem>
                  ) : (
                    corporatePrograms.map((program) => (
                      <SelectItem key={program.id} value={program.program}>
                        {program.program_details?.name || program.program_name || 'Program'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FieldError message={fieldErrors.programId} />
            </div>

            <div className="space-y-3">
              <Label>Add-ons</Label>
              {isLoadingAddons ? (
                <p className="text-sm text-gray-500">Loading add-ons...</p>
              ) : corporateAddons.length === 0 ? (
                <p className="text-sm text-gray-500">No add-ons available</p>
              ) : (
                <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
                  {corporateAddons.map((addon) => {
                    const addonId = String(
                      addon.addon_details?.addon_id || addon.addon || addon.id
                    );
                    const isChecked = selectedAddonIds.includes(addonId);
                    return (
                      <label
                        key={addon.id}
                        className="flex items-start gap-2 rounded-lg border border-gray-200 p-3 text-sm"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const nextChecked = checked === true;
                            setSelectedAddonIds((prev) =>
                              nextChecked ? [...prev, addonId] : prev.filter((id) => id !== addonId)
                            );
                          }}
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {addon.addon_details?.name || addon.addon_name || 'Add-on'}
                          </p>
                          {addon.addon_details?.description && (
                            <p className="text-xs text-gray-500">
                              {addon.addon_details.description}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Invite
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteEmployeeModal;
